import React, { useCallback, useMemo, useState } from 'react';

import { Alert, LinearProgress, Pagination, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { CharterFareInventory, CharterInventoryFlight } from '~/components/Flights/Charter/charter.types';

import * as TimeService from '~/services/TimeService';

import { CharterInventoryFilters, CharterInventoryFormState } from './CharterInventoryFilters';
import DepartingFlightsCard from './DepartingFlightsCard';
import ReturningFlightsCard from './ReturningFlightsCard';
import { CharterInventoryCsvHeader, cabinOptions, defaultMonths, stayOptions } from './charterInventoryOptions';
import {
  availableSeats,
  blockedSeats,
  blockedSeatsByReason,
  cabinLabel,
  formatDate,
  formatTime,
} from './charterInventoryUtils';
import {
  CharterInventoryFilters as CharterInventoryFiltersType,
  PairedFlightRow,
  useCharterInventory,
} from './useCharterInventory';

const defaultFormState: CharterInventoryFormState = {
  departureAirport: 'MEL',
  arrivalAirport: 'MLE',
  returnDepartureAirport: 'MLE',
  returnArrivalAirport: 'MEL',
  stayDurations: stayOptions.map((option) => option.value),
  cabinClasses: cabinOptions.map((option) => option.value),
  departingMonths: defaultMonths,
  startDate: null,
  endDate: null,
};

const buildCsvRows = (
  pairedRows: Array<PairedFlightRow>,
  columns: Array<CharterInventoryCsvHeader>,
  downloadTimestamp: Date,
) => {
  const downloadDate = downloadTimestamp.toLocaleDateString('en-AU');
  const downloadTime = downloadTimestamp.toLocaleTimeString('en-AU', { hour12: false });

  const getColumnValue = (
    column: CharterInventoryCsvHeader,
    direction: 'Departing' | 'Returning',
    flight: CharterInventoryFlight,
    fare: CharterFareInventory,
    stayDuration: number,
  ) => {
    switch (column) {
      case 'Direction':
        return direction;
      case 'Departure Date':
        return formatDate(flight.depDate);
      case 'Departure Time':
        return formatTime(flight.depTime);
      case 'Arrival Date':
        return formatDate(flight.arrDate);
      case 'Arrival Time':
        return formatTime(flight.arrTime);
      case 'Departure Airport Code':
        return flight.depAirport;
      case 'Arrival Airport Code':
        return flight.arrAirport;
      case 'Cabin Class':
        return cabinLabel[fare.cabinClass] ?? fare.cabinClass;
      case 'Booking Class':
        return fare.bookingClass;
      case 'Total Bookable Seats':
        return fare.allocation;
      case 'Sold Seats':
        return fare.booked;
      case 'Total Blocked Seats':
        return blockedSeats(flight, fare);
      case 'Available Seats':
        return availableSeats(fare);
      case 'Currency':
        return fare.currency ?? '';
      case 'Seat Price (Adult)':
        return fare.priceAdult.toFixed(2);
      case 'Seat Price (Child)':
        return fare.priceChild.toFixed(2);
      case 'Seat Price (Infant)':
        return fare.priceInfant.toFixed(2);
      case 'Tax (Adult)':
        return fare.taxAdult.toFixed(2);
      case 'Tax (Child)':
        return fare.taxChild.toFixed(2);
      case 'Tax (Infant)':
        return fare.taxInfant.toFixed(2);
      case 'Blocked Seats (VVIP)':
        return blockedSeatsByReason(flight, fare, 'VVIP');
      case 'Blocked Seats (Crew Rest)':
        return blockedSeatsByReason(flight, fare, 'CREW_REST');
      case 'Blocked Seats (Dummy)':
        return blockedSeatsByReason(flight, fare, 'DUMMY');
      case 'Passenger Count (Adult)':
        return flight.passengerCounts.adult;
      case 'Passenger Count (Child)':
        return flight.passengerCounts.child;
      case 'Passenger Count (Infant)':
        return flight.passengerCounts.infant;
      default:
        return '';
    }
  };

  const buildRow = (row: PairedFlightRow) => {
    const depRows = row.departing.fareInventory.map((fare) => [
      downloadDate,
      downloadTime,
      ...columns.map((col) => getColumnValue(col, 'Departing', row.departing, fare, row.stayDuration)),
    ]);

    const retRows = row.returning
      ? row.returning.fareInventory.map((fare) => [
          downloadDate,
          downloadTime,
          ...columns.map((col) => getColumnValue(col, 'Returning', row.returning, fare, row.stayDuration)),
        ])
      : [];

    return [...depRows, ...retRows];
  };

  return [['Download Date', 'Download Time', ...columns], ...pairedRows.flatMap(buildRow)];
};

const triggerCsvDownload = (rows: Array<Array<unknown>>, filename: string) => {
  const csvContent = rows
    .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default function CharterInventoryPage(): React.JSX.Element {
  const [formState, setFormState] = useState<CharterInventoryFormState>(defaultFormState);
  const [searchFilters, setSearchFilters] = useState<CharterInventoryFiltersType | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { pairedRows, canDownload, noResults, queryError, isFetching } = useCharterInventory(searchFilters);

  const handleSearch = useCallback(() => {
    const departure = formState.departureAirport;
    const arrival = formState.arrivalAirport;
    const returnDeparture = formState.returnDepartureAirport;
    const returnArrival = formState.returnArrivalAirport;

    if (!departure || !arrival || !returnDeparture || !returnArrival) {
      return;
    }

    setPage(1);
    setFormState((current) => ({
      ...current,
      departureAirport: departure,
      arrivalAirport: arrival,
      returnDepartureAirport: returnDeparture,
      returnArrivalAirport: returnArrival,
    }));

    setSearchFilters({
      departureAirport: departure,
      arrivalAirport: arrival,
      returnDepartureAirport: returnDeparture,
      returnArrivalAirport: returnArrival,
      stayDurations: formState.stayDurations.length
        ? formState.stayDurations
        : stayOptions.map((option) => option.value),
      cabinClasses: formState.cabinClasses.length ? formState.cabinClasses : cabinOptions.map((option) => option.value),
      departingMonths: defaultMonths,
      startDate: formState.startDate ? TimeService.formatDateISO(formState.startDate) : undefined,
      endDate: formState.endDate ? TimeService.formatDateISO(formState.endDate) : undefined,
    });
  }, [formState]);

  const handleDownload = useCallback(
    (columns: Array<CharterInventoryCsvHeader>) => {
      if (!canDownload || !searchFilters) {
        return;
      }

      const downloadTimestamp = new Date();
      const rows = buildCsvRows(pairedRows, columns, downloadTimestamp);
      const dateStr = downloadTimestamp.toISOString().slice(0, 16).replace('T', '-').replace(':', '-');
      const route = `${searchFilters.departureAirport}-${searchFilters.arrivalAirport}`;

      triggerCsvDownload(rows, `charter-inventory-${route}-${dateStr}.csv`);
    },
    [canDownload, pairedRows, searchFilters],
  );

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  // Pagination based on expanded row count (each PairedFlightRow produces fareInventory.length table rows)
  const { paginatedRows, totalExpandedRowCount } = useMemo(() => {
    const totalExpanded = pairedRows.reduce((sum, row) => sum + row.departing.fareInventory.length, 0);

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    const result: Array<PairedFlightRow> = [];
    let accumulatedRows = 0;

    for (const row of pairedRows) {
      const fareCount = row.departing.fareInventory.length;
      const rowStart = accumulatedRows;
      const rowEnd = accumulatedRows + fareCount;

      // Include this PairedFlightRow if it overlaps with the page range
      if (rowStart < endIndex && rowEnd > startIndex) {
        // Calculate slice indices relative to the flight's fare list
        const sliceStart = Math.max(0, startIndex - rowStart);
        const sliceEnd = Math.min(fareCount, endIndex - rowStart);

        // Clone the row and slice the fare inventories so strict pagination is respected
        const slicedRow: PairedFlightRow = {
          ...row,
          departing: {
            ...row.departing,
            fareInventory: row.departing.fareInventory.slice(sliceStart, sliceEnd),
          },
          returning: row.returning
            ? {
                ...row.returning,
                fareInventory: row.returning.fareInventory.slice(sliceStart, sliceEnd),
              }
            : undefined,
        };

        result.push(slicedRow);
      }

      accumulatedRows += fareCount;

      // Early exit once we've passed the current page
      if (accumulatedRows >= endIndex) {
        break;
      }
    }

    return { paginatedRows: result, totalExpandedRowCount: totalExpanded };
  }, [pairedRows, page, pageSize]);

  const returnRouteLabel = useMemo(() => {
    if (searchFilters) {
      return `${searchFilters.returnDepartureAirport} → ${searchFilters.returnArrivalAirport}`;
    }

    return `${formState.returnDepartureAirport} → ${formState.returnArrivalAirport}`;
  }, [formState.returnArrivalAirport, formState.returnDepartureAirport, searchFilters]);

  const departRouteLabel = useMemo(() => {
    if (searchFilters) {
      return `${searchFilters.departureAirport} → ${searchFilters.arrivalAirport}`;
    }

    return `${formState.departureAirport} → ${formState.arrivalAirport}`;
  }, [formState.arrivalAirport, formState.departureAirport, searchFilters]);

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Charter inventory
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search charter flight inventory by journey, cabin, stay duration, and departure dates. Returning flights are
          paired by aircraft and stay duration so you can review allocations side by side.
        </Typography>
      </Stack>

      <CharterInventoryFilters
        value={formState}
        onChange={setFormState}
        onSearch={handleSearch}
        onDownload={handleDownload}
        canDownload={canDownload}
        isSearching={isFetching}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {queryError ? <Alert severity="error">{queryError.message}</Alert> : null}
      {isFetching ? <LinearProgress sx={{ maxWidth: 320 }} /> : null}
      {noResults ? <Alert severity="info">No flights found for the selected filters.</Alert> : null}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DepartingFlightsCard
            rows={paginatedRows}
            label={departRouteLabel}
            stayDurations={searchFilters?.stayDurations}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ReturningFlightsCard rows={paginatedRows} label={returnRouteLabel} />
        </Grid>
      </Grid>

      {totalExpandedRowCount > pageSize ? (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={Math.ceil(totalExpandedRowCount / pageSize)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
